import type { MessageDescriptor } from 'react-intl';

import type { CollabGraphRecommendedEntity } from '../../../utils/api/collaborationUsers';
import type { OrgMetricsResponse } from '../../../utils/api/orgMetrics';
import type {
	BatchOutcome,
	SharedWorkEntity,
	UserMicrositeHeroMetricsResponse,
} from '../../../utils/api/userMetrics';

import { messages } from './messages';

/**
 * Wrap a raw ARI in a `SharedWorkEntity` for the static datasets. The
 * Flock-backed BE now returns hydrated objects (with `displayName`,
 * `url`, `__typename`, etc.) per the integration guide; the debug
 * fixtures never hit the network so we synthesise a plausible-looking
 * payload from the ARI alone.
 *
 * - `__typename` is best-effort derived from the ARI's resource segment
 *   (e.g. `ari:cloud:confluence:…:page/…` → `'page'`). Consumers like
 *   `getProductLogoUrl` look at the ARI prefix first, so a precise
 *   `__typename` is not required to render the right logo.
 * - `displayName` is a humanised tail of the ARI so the UI has
 *   *something* meaningful to show; engineers wanting realistic copy
 *   should override the field directly in the dataset.
 * - `url` is set to `'#'` so links render but never navigate during
 *   static preview.
 */
const sharedItem = (ari: string, overrides?: Partial<SharedWorkEntity>): SharedWorkEntity => {
	const resourceSegment = ari.split('::').pop()?.split(':').pop() ?? '';
	const [typenameFromAri = undefined, idTail = ''] = resourceSegment.split('/');
	return {
		ari,
		__typename: typenameFromAri,
		displayName: idTail || ari,
		url: '#',
		...overrides,
	};
};

/**
 * Identifier for which debug-panel tier (and therefore which home-page
 * view) the user has chosen to preview. Each tier has its own list of
 * static datasets and, when selected, forces `LoggedInView` to render the
 * matching view regardless of the live gating logic.
 *
 * - `tier1` → `UserGraphView` (user-scoped metrics).
 * - `tier2` → `OrgGraphView` (org-scoped metrics).
 * - `tier3` → `GettingStartedView` (capability showcase; no real data).
 */
export type DebugTier = 'tier1' | 'tier2' | 'tier3';

///////////////////////////////////////
/////////      TIER 1      ////////////
///////////////////////////////////////
/**
 * Identifier for a static Tier 1 (user-scoped) dataset. Selecting one of
 * these routes the UI to `UserGraphView` and seeds `UserMetricsAtom` with
 * the corresponding `UserMicrositeHeroMetricsResponse` payload.
 */
export type StaticTier1DatasetKey =
	| 'powerUser'
	| 'engineerIC'
	| 'designerIC'
	| 'productLead'
	| 'newContributor';

/**
 * UI-facing description of a single static Tier 1 dataset.
 */
export type StaticTier1Dataset = {
	key: StaticTier1DatasetKey;
	label: MessageDescriptor;
	description: MessageDescriptor;
	metrics: UserMicrositeHeroMetricsResponse;
};

const TIER1_COMPUTED_AT = '2026-05-02T12:32:06.945Z';

/**
 * Batch outcomes used by every Tier 1 fixture. The static datasets are
 * always "fully successful" so the debug panel preview never has to render
 * the partial-failure UI by accident — engineers verifying that branch
 * should construct an ad-hoc payload instead of relying on these.
 */
const TIER1_BATCH_OUTCOMES_FULFILLED: UserMicrositeHeroMetricsResponse['meta']['batchOutcomes'] = {
	relationshipsByProvider: 'fulfilled',
	peopleWorkedWith: 'fulfilled',
	relationshipsCreated180d: 'fulfilled',
};

/**
 * Sample collaborator ARIs used by every Tier 1 fixture's
 * `workByCollaborators` map. Mirrors the first three entries of
 * `STATIC_COLLABORATION_USERS` so the demo data is internally consistent.
 */
const TIER1_COLLABORATOR_ARIS = [
	'ari:cloud:identity::user/712020:fe8c7aeb-92b9-4075-bfed-e4d859bb587f',
	'ari:cloud:identity::user/70121:f604320b-d57f-4347-9af4-6134863803c2',
	'ari:cloud:identity::user/63749bac9960988ef6bc6096',
] as const;

/**
 * Static Tier 1 datasets. Each persona represents a different shape of
 * user activity and is hand-crafted to comfortably pass the Tier 1 coverage
 * floor of `evaluateUserTier` (which now derives counts from
 * `relationshipsByProvider` rather than reading hand-rolled top-level
 * fields), so `UserGraphView` renders with meaningful numbers across its
 * stat grid and graph sections.
 *
 * The shape mirrors the Flock-backed
 * `GET /twg-api/v1/microsite/metrics/user` response — first-party providers
 * (`confluence`, `jira`, `loom`, `atlas`, `code`) live alongside whichever
 * third-party providers each persona would realistically have linked.
 *
 * Values are hand-crafted (not sampled from a real tenant) — they are
 * only used to seed `UserMetricsAtom` when the debug panel is in
 * "static" mode with Tier 1 selected.
 */
export const STATIC_TIER1_DATASETS: Record<StaticTier1DatasetKey, StaticTier1Dataset> = {
	powerUser: {
		key: 'powerUser',
		label: messages.datasetTier1PowerUserLabel,
		description: messages.datasetTier1PowerUserDescription,
		metrics: {
			workByCollaborators: {
				'ari:cloud:identity::user/712020:fe8c7aeb-92b9-4075-bfed-e4d859bb587f': [
					sharedItem(
						'ari:cloud:confluence:DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5:page/455657226241',
					),
				],
				'ari:cloud:identity::user/70121:f604320b-d57f-4347-9af4-6134863803c2': [
					sharedItem(
						'ari:cloud:confluence:DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5:page/455593164839',
					),
					sharedItem(
						'ari:cloud:confluence:DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5:page/455593197628',
					),
				],
				'ari:cloud:identity::user/63749bac9960988ef6bc6096': [
					sharedItem('ari:cloud:jira:DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5:issue/12553'),
					sharedItem(
						'ari:cloud:townsquare:DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5:project/82911173-82cc-431f-baf4-1bb758b3c7be',
					),
				],
			},
			relationshipsByProvider: {
				confluence: { page: 312, comment: 600, whiteboard: 12, space: 4, database: 0 },
				'jira-dc': { workItem: 427, comment: 400, release: 18 },
				loom: { video: 96, comment: 8 },
				atlas: {
					goal: 14,
					project: 26,
					focusArea: 6,
					goalUpdate: 38,
					projectUpdate: 56,
					comment: 12,
				},
				code: { comment: 184 },
				github: { pullRequest: 214, commit: 712, branch: 96, repository: 8 },
				figma: { design: 38 },
				slack: { message: 5482 },
				'google-drive': { page: 3174 },
			},
			peopleWorkedWithCount: 87,
			relationshipsCreated180d: 184220,
			computedAt: TIER1_COMPUTED_AT,
			meta: { batchOutcomes: TIER1_BATCH_OUTCOMES_FULFILLED },
		},
	},
	engineerIC: {
		key: 'engineerIC',
		label: messages.datasetTier1EngineerICLabel,
		description: messages.datasetTier1EngineerICDescription,
		metrics: {
			workByCollaborators: {
				[TIER1_COLLABORATOR_ARIS[0]]: [
					sharedItem('ari:cloud:bitbucket:DUMMY-engineerIC:repository/twg-api'),
					sharedItem('ari:cloud:jira:DUMMY-engineerIC:issue/ENG-2118'),
				],
				[TIER1_COLLABORATOR_ARIS[1]]: [
					sharedItem('ari:cloud:jira:DUMMY-engineerIC:issue/ENG-2204'),
				],
			},
			relationshipsByProvider: {
				confluence: { page: 58, comment: 380, whiteboard: 4, space: 1 },
				jira: { workItem: 198, comment: 280, release: 8 },
				loom: { video: 28, comment: 2 },
				atlas: { goal: 0, project: 1, focusArea: 0, goalUpdate: 1, projectUpdate: 4, comment: 4 },
				code: { comment: 76 },
				github: { pullRequest: 163, commit: 540, branch: 72, repository: 5 },
				figma: { design: 8 },
				slack: { message: 1874 },
				sentry: { workItem: 231 },
			},
			peopleWorkedWithCount: 42,
			relationshipsCreated180d: 61440,
			computedAt: TIER1_COMPUTED_AT,
			meta: { batchOutcomes: TIER1_BATCH_OUTCOMES_FULFILLED },
		},
	},
	designerIC: {
		key: 'designerIC',
		label: messages.datasetTier1DesignerICLabel,
		description: messages.datasetTier1DesignerICDescription,
		metrics: {
			workByCollaborators: {
				[TIER1_COLLABORATOR_ARIS[0]]: [
					sharedItem('ari:cloud:figma:DUMMY-designerIC:design/teamwork-graph-system'),
					sharedItem('ari:cloud:confluence:DUMMY-designerIC:page/8842055120'),
				],
				[TIER1_COLLABORATOR_ARIS[2]]: [
					sharedItem('ari:cloud:figma:DUMMY-designerIC:design/microsite-tier1-flow'),
				],
			},
			relationshipsByProvider: {
				confluence: { page: 74, comment: 240, whiteboard: 8, space: 1 },
				jira: { workItem: 46, comment: 80 },
				loom: { video: 84, comment: 16 },
				atlas: { goal: 4, project: 8, projectUpdate: 12, comment: 6 },
				code: { comment: 0 },
				github: { pullRequest: 17, commit: 24, branch: 6, repository: 2 },
				figma: { design: 192 },
				slack: { message: 1102 },
				'google-drive': { page: 842 },
			},
			peopleWorkedWithCount: 35,
			relationshipsCreated180d: 42118,
			computedAt: TIER1_COMPUTED_AT,
			meta: { batchOutcomes: TIER1_BATCH_OUTCOMES_FULFILLED },
		},
	},
	productLead: {
		key: 'productLead',
		label: messages.datasetTier1ProductLeadLabel,
		description: messages.datasetTier1ProductLeadDescription,
		metrics: {
			workByCollaborators: {
				[TIER1_COLLABORATOR_ARIS[0]]: [
					sharedItem('ari:cloud:townsquare:DUMMY-productLead:project/h2-roadmap'),
					sharedItem('ari:cloud:confluence:DUMMY-productLead:page/8842061177'),
				],
				[TIER1_COLLABORATOR_ARIS[1]]: [
					sharedItem('ari:cloud:jira:DUMMY-productLead:issue/PRD-908'),
				],
				[TIER1_COLLABORATOR_ARIS[2]]: [
					sharedItem('ari:cloud:townsquare:DUMMY-productLead:goal/north-star-2026'),
				],
			},
			relationshipsByProvider: {
				confluence: { page: 204, comment: 540, whiteboard: 6, space: 2 },
				jira: { workItem: 316, comment: 320 },
				loom: { video: 58, comment: 8 },
				atlas: {
					goal: 8,
					project: 18,
					focusArea: 4,
					goalUpdate: 22,
					projectUpdate: 34,
					comment: 18,
				},
				code: { comment: 110 },
				github: { pullRequest: 21, commit: 38, branch: 4, repository: 1 },
				figma: { design: 9 },
				slack: { message: 4318 },
				'google-calendar': { calendarEvent: 1274 },
				'google-drive': { page: 1623 },
			},
			peopleWorkedWithCount: 64,
			relationshipsCreated180d: 38742,
			computedAt: TIER1_COMPUTED_AT,
			meta: { batchOutcomes: TIER1_BATCH_OUTCOMES_FULFILLED },
		},
	},
	newContributor: {
		key: 'newContributor',
		label: messages.datasetTier1NewContributorLabel,
		description: messages.datasetTier1NewContributorDescription,
		metrics: {
			// New contributor naturally works alone — leave
			// `workByCollaborators` omitted to exercise the "header was sent
			// but no shared work found" branch downstream.
			relationshipsByProvider: {
				confluence: { page: 4, comment: 3, whiteboard: 0 },
				jira: { workItem: 0, comment: 2 },
				loom: { video: 0, comment: 1 },
				atlas: { project: 0 },
				code: { comment: 4 },
				github: { pullRequest: 0, commit: 0, branch: 0, repository: 0 },
				figma: { design: 0 },
				slack: { message: 3 },
				'google-drive': { page: 2 },
			},
			peopleWorkedWithCount: 3,
			relationshipsCreated180d: 19,
			computedAt: TIER1_COMPUTED_AT,
			meta: { batchOutcomes: TIER1_BATCH_OUTCOMES_FULFILLED },
		},
	},
};

// This is only used when tier 3 is being debugged
export const TIER_3_USER_DATASET: {
	key: string;
	label: MessageDescriptor;
	description: MessageDescriptor;
	metrics: {
		// New contributor naturally works alone — leave
		// `workByCollaborators` omitted to exercise the "header was sent
		// but no shared work found" branch downstream.
		relationshipsByProvider: {
			confluence: {
				page: number;
				comment: number;
				whiteboard: number;
			};
			jira: {
				workItem: number;
				comment: number;
			};
			loom: {
				video: number;
				comment: number;
			};
		};
		peopleWorkedWithCount: number;
		relationshipsCreated180d: number;
		computedAt: string;
		meta: {
			batchOutcomes: {
				relationshipsByProvider: BatchOutcome;
				peopleWorkedWith: BatchOutcome;
				relationshipsCreated180d: BatchOutcome;
			};
		};
	};
} = {
	key: 'tier3Contributor',
	label: messages.datasetTier1NewContributorLabel,
	description: messages.datasetTier1NewContributorDescription,
	metrics: {
		// New contributor naturally works alone — leave
		// `workByCollaborators` omitted to exercise the "header was sent
		// but no shared work found" branch downstream.
		relationshipsByProvider: {
			confluence: { page: 4, comment: 3, whiteboard: 0 },
			jira: { workItem: 0, comment: 2 },
			loom: { video: 0, comment: 1 },
		},
		peopleWorkedWithCount: 3,
		relationshipsCreated180d: 19,
		computedAt: TIER1_COMPUTED_AT,
		meta: { batchOutcomes: TIER1_BATCH_OUTCOMES_FULFILLED },
	},
};

/**
 * Ordered list of Tier 1 datasets for the debug panel UI.
 */
export const STATIC_TIER1_DATASET_LIST: ReadonlyArray<StaticTier1Dataset> = [
	STATIC_TIER1_DATASETS.powerUser,
	STATIC_TIER1_DATASETS.engineerIC,
	STATIC_TIER1_DATASETS.designerIC,
	STATIC_TIER1_DATASETS.productLead,
	STATIC_TIER1_DATASETS.newContributor,
];

///////////////////////////////////////
/////////      TIER 2      ////////////
///////////////////////////////////////

/**
 * Identifier for a static org-metrics dataset bundled with the debug panel.
 *
 * These datasets mirror rows from the "Sample metrics by organisation"
 * Confluence page (`6858084748`) and are used by the debug panel to seed
 * `OrgMetricsAtom` without hitting the live `metrics/org/{orgId}` endpoint.
 *
 * These are the Tier 2 datasets — selecting one routes the UI to
 * `OrgGraphView`.
 */
export type StaticTier2DatasetKey =
	| 'fastMed'
	| 'accenture'
	| 'allegion'
	| 'atlassian'
	| 'communardo'
	| 'reddit'
	| 'procoreLargest'
	| 'helloFresh'
	| 'workday'
	| 'workdayLargest';

/**
 * UI-facing description of a single static Tier 2 dataset.
 */
export type StaticTier2Dataset = {
	key: StaticTier2DatasetKey;
	label: MessageDescriptor;
	description: MessageDescriptor;
	metrics: OrgMetricsResponse;
};

const COMPUTED_AT = '2026-04-23T07:54:06.392+00:00';

/**
 * Static datasets sourced from Confluence page 6858084748 ("Sample metrics by
 * organisation"). Numeric strings from the source table have been parsed to
 * numbers, `-` has been mapped to `null`, and obvious source typos (e.g.
 * "2026-04-1", "167, 966", "18, 519") have been normalised to their intended
 * values.
 *
 * `cacheStatus`, `daysSinceSnapshot`, `estimatedNodesNow`, `commentsDataAsOf`,
 * `meetingNotesDataAsOf`, `connectorDataAsOf`, `smartlinkDataAsOf`,
 * `forgeAppsInstalled`, and per-user fields (`entitiesTouched`, `collaborators`,
 * `collaboratorTeams`, `collaboratorTeamsTruncated`) are not part of the source
 * table — they are derived/operational fields supplied by the BE and are left
 * as `null` here.
 */
export const STATIC_TIER2_DATASETS: Record<StaticTier2DatasetKey, StaticTier2Dataset> = {
	/**
	 * Synthetic "Fast Med" healthcare tenant — every metric needed to
	 * exercise the inferred-1p-apps fallback in `ConnectedAppsSection`.
	 *
	 * The defining feature: `connectorObjectsByProvider: null`,
	 * `threePConnectorsInstalled: 0`, `connectorsActive28d: 0` —
	 * combined with a `KnowledgeSourcesAtom` that returns no connected
	 * apps, this triggers `infer1pAppsFromOrgMetrics` to surface
	 * Confluence (pages), Jira (work items), Loom (recordings),
	 * Bitbucket (jira-code-links) and Atlas (goal-work-item links) as
	 * the org's "favourite apps".
	 *
	 * Activity counts are deliberately mid-sized so the panel renders
	 * realistic numbers without dominating the rest of the dashboard.
	 */
	fastMed: {
		key: 'fastMed',
		label: messages.datasetFastMedLabel,
		description: messages.datasetFastMedDescription,
		metrics: {
			activeContributors180d: 412,
			activeNodeTypes: 28,
			activeRelTypes: 96,
			activityDataAsOf: '2026-04-17',
			avgArtifactsPerIssue: 1.4,
			avgDailyJiraCodeLinkChange: 86,
			avgDailyNodeGrowth: 5210,
			avgDailyRelChange: 18430,
			avgFetchQueriesPerDay: 540,
			avgFetchQueriesPerSecond: 0.00625,
			avgJiraCodeLinkChangePerSecond: 0.001,
			avgNodeGrowthPerSecond: 0.0603,
			avgRelChangePerSecond: 0.2133,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 14_582,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			// No 3P connectors — this is the gate that makes the
			// inferred-1p-apps fallback fire.
			connectorObjects180d: 0,
			connectorObjectsByProvider: null,
			connectorsActive28d: 0,
			connectorsDetail: null,
			connectorsInstalled: 0,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			// Atlas signal — goal ↔ work item links exist, so Atlas is
			// inferred.
			goalWorkItemLinks: 248,
			issueActivityDataAsOf: '2026-04-21',
			// Jira signal — work items moved forward.
			issuesMovedForward180d: 6_842,
			// Bitbucket signal — Jira ↔ code links exist.
			jiraCodeLinks: 12_540,
			loomDataAsOf: '2026-04-21T12:14:21.319+00:00',
			// Loom signal — recordings made in the last 180d.
			loomsRecorded180d: 318,
			marketplaceAddonsInstalled: 12,
			meetingMinutesSaved: 1_240,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 22,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 5,
			organisationId: 'fast-med-org-no-3p-fallback-fixture',
			// Confluence signal — pages created in the last 180d.
			pagesCreated180d: 8_915,
			pagesDataAsOf: '2026-04-18',
			pctConnectorsActive28d: 0,
			queryDataAsOf: null,
			relDataAsOf: '2026-04-18',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 612,
			teamCount: 96,
			threePConnectorsInstalled: 0,
			totalAppsInstalled: 5,
			totalJiraIssues: 184_320,
			totalMinutesRecorded: 1_540,
			totalNodes: 612_400,
			totalRelationships: 2_184_900,
			userCount: 845,
		},
	},
	accenture: {
		key: 'accenture',
		label: messages.datasetAccentureLabel,
		description: messages.datasetAccentureDescription,
		metrics: {
			activeContributors180d: 0,
			activeNodeTypes: 71,
			activeRelTypes: 235,
			activityDataAsOf: '2026-04-17',
			avgArtifactsPerIssue: 0.7,
			avgDailyJiraCodeLinkChange: 1107,
			avgDailyNodeGrowth: 70638,
			avgDailyRelChange: 643225,
			avgFetchQueriesPerDay: 1421,
			avgFetchQueriesPerSecond: 0.0164,
			avgJiraCodeLinkChangePerSecond: 0.0128,
			avgNodeGrowthPerSecond: 0.8176,
			avgRelChangePerSecond: 7.4447,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 29,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 790,
			connectorObjectsByProvider: [
				{ providerName: 'Slack provider', objects30d: 719 },
				{ providerName: 'Figma', objects30d: 71 },
			],
			connectorsActive28d: 0,
			connectorsDetail: null,
			connectorsInstalled: 0,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 4919,
			issueActivityDataAsOf: '2026-04-21',
			issuesMovedForward180d: 1855,
			jiraCodeLinks: 1136203,
			loomDataAsOf: '2026-04-21T12:14:21.319+00:00',
			loomsRecorded180d: 2,
			marketplaceAddonsInstalled: 292,
			meetingMinutesSaved: 48,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 0,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 13,
			organisationId: 'a53d6b63-3k57-1408-j79d-0b60b08kb685',
			pagesCreated180d: 167966,
			pagesDataAsOf: '2026-04-18',
			pctConnectorsActive28d: 0,
			queryDataAsOf: null,
			relDataAsOf: '2026-04-18',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 0,
			teamCount: 13750,
			threePConnectorsInstalled: 0,
			totalAppsInstalled: 305,
			totalJiraIssues: 4844413,
			totalMinutesRecorded: 32,
			totalNodes: 24456310,
			totalRelationships: 107366741,
			userCount: 12358,
		},
	},
	allegion: {
		key: 'allegion',
		label: messages.datasetAllegionLabel,
		description: messages.datasetAllegionDescription,
		metrics: {
			activeContributors180d: 1,
			activeNodeTypes: 51,
			activeRelTypes: 152,
			activityDataAsOf: '2026-04-19',
			avgArtifactsPerIssue: 2.8,
			avgDailyJiraCodeLinkChange: 312,
			avgDailyNodeGrowth: 1426,
			avgDailyRelChange: 13365,
			avgFetchQueriesPerDay: 6834,
			avgFetchQueriesPerSecond: 0.0791,
			avgJiraCodeLinkChangePerSecond: 0.0036,
			avgNodeGrowthPerSecond: 0.0165,
			avgRelChangePerSecond: 0.1547,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 664,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 107702,
			connectorObjectsByProvider: [
				{ providerName: 'GitHub for Jira', objects30d: 106757 },
				{ providerName: 'Figma', objects30d: 589 },
				{ providerName: 'Azure Devops provider', objects30d: 243 },
				{ providerName: 'Mural', objects30d: 79 },
				{ providerName: 'Bitbucket Pipelines Integration', objects30d: 15 },
				{ providerName: 'Gitlab provider', objects30d: 8 },
				{ providerName: 'Adobe', objects30d: 7 },
				{ providerName: 'Version Related Links app', objects30d: 4 },
			],
			connectorsActive28d: 0,
			connectorsDetail: null,
			connectorsInstalled: 0,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 0,
			issueActivityDataAsOf: '2026-04-18',
			issuesMovedForward180d: 462,
			jiraCodeLinks: 31049,
			loomDataAsOf: null,
			loomsRecorded180d: 0,
			marketplaceAddonsInstalled: 4,
			meetingMinutesSaved: 0,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 0,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 8,
			organisationId: 'b588d5e7-4ecd-4761-ba49-53e2027dea76',
			pagesCreated180d: 30399,
			pagesDataAsOf: '2026-04-19',
			pctConnectorsActive28d: 0,
			queryDataAsOf: '2026-04-19',
			relDataAsOf: '2026-04-19',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 0,
			teamCount: 98,
			threePConnectorsInstalled: 0,
			totalAppsInstalled: 12,
			totalJiraIssues: 17952,
			totalMinutesRecorded: 0,
			totalNodes: 315970,
			totalRelationships: 1369930,
			userCount: 138,
		},
	},
	atlassian: {
		key: 'atlassian',
		label: messages.datasetAtlassianLabel,
		description: messages.datasetAtlassianDescription,
		metrics: {
			activeContributors180d: 7902,
			activeNodeTypes: 96,
			activeRelTypes: 356,
			activityDataAsOf: '2026-04-18',
			avgArtifactsPerIssue: 37.1,
			avgDailyJiraCodeLinkChange: 206668,
			avgDailyNodeGrowth: 9897860,
			avgDailyRelChange: 10361407,
			avgFetchQueriesPerDay: 6019720,
			avgFetchQueriesPerSecond: 69.6727,
			avgJiraCodeLinkChangePerSecond: 2.392,
			avgNodeGrowthPerSecond: 114.5586,
			avgRelChangePerSecond: 119.9237,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 2258042,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 9608,
			connectorObjectsByProvider: [
				{ providerName: 'Google Drive provider', objects30d: 7392 },
				{ providerName: 'Slack provider', objects30d: 1740 },
				{ providerName: 'Salesforce', objects30d: 441 },
				{ providerName: 'Docusign provider', objects30d: 31 },
				{ providerName: 'Bitbucket Pipelines Integration', objects30d: 4 },
			],
			connectorsActive28d: 11,
			connectorsDetail: null,
			connectorsInstalled: 15,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 41083,
			issueActivityDataAsOf: '2026-04-18',
			issuesMovedForward180d: 317494,
			jiraCodeLinks: 56152610,
			loomDataAsOf: '2026-04-22T23:59:51.278+00:00',
			loomsRecorded180d: 132785,
			marketplaceAddonsInstalled: 5,
			meetingMinutesSaved: 3591291,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 1527,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 9,
			organisationId: '2346a038-3c8c-498b-a79b-e7847859868d',
			pagesCreated180d: 5068524,
			pagesDataAsOf: '2026-04-18',
			pctConnectorsActive28d: 73.3,
			queryDataAsOf: '2026-04-18',
			relDataAsOf: '2026-04-18',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 52,
			teamCount: 26522,
			threePConnectorsInstalled: 15,
			totalAppsInstalled: 29,
			totalJiraIssues: 1739733,
			totalMinutesRecorded: 2394194,
			totalNodes: 1642078616,
			totalRelationships: 4465869234,
			userCount: 46491,
		},
	},
	communardo: {
		key: 'communardo',
		label: messages.datasetCommunardoLabel,
		description: messages.datasetCommunardoDescription,
		metrics: {
			activeContributors180d: 31,
			activeNodeTypes: 66,
			activeRelTypes: 206,
			activityDataAsOf: '2026-04-18',
			avgArtifactsPerIssue: 7.1,
			avgDailyJiraCodeLinkChange: 94,
			avgDailyNodeGrowth: 1732,
			avgDailyRelChange: 115491,
			avgFetchQueriesPerDay: 29473,
			avgFetchQueriesPerSecond: 0.3411,
			avgJiraCodeLinkChangePerSecond: 0.0011,
			avgNodeGrowthPerSecond: 0.02,
			avgRelChangePerSecond: 1.3367,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 5143,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 2338235,
			connectorObjectsByProvider: [
				{ providerName: 'OneDrive provider', objects30d: 1106642 },
				{ providerName: 'Sharepoint provider', objects30d: 630301 },
				{ providerName: 'MS Teams provider', objects30d: 550977 },
				{ providerName: 'Bitbucket Pipelines Integration', objects30d: 24752 },
				{ providerName: 'Google Drive provider', objects30d: 20483 },
				{ providerName: 'Snyk Security in Jira Cloud', objects30d: 3787 },
				{ providerName: 'Jira-Cloud-Bitbucket-OnPrem', objects30d: 742 },
				{ providerName: 'Loom', objects30d: 405 },
				{ providerName: 'Figma', objects30d: 65 },
				{ providerName: 'Power-BI', objects30d: 54 },
				{ providerName: 'Version Related Links app', objects30d: 27 },
			],
			connectorsActive28d: 2,
			connectorsDetail: null,
			connectorsInstalled: 3,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 160,
			issueActivityDataAsOf: '2026-04-18',
			issuesMovedForward180d: 6075,
			jiraCodeLinks: 85239,
			loomDataAsOf: '2026-04-17T14:36:42.367+00:00',
			loomsRecorded180d: 92,
			marketplaceAddonsInstalled: 60,
			meetingMinutesSaved: 5346,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 4,
			nodeDataAsOf: '2026-04-1',
			onePAppsInstalled: 11,
			organisationId: '3cc38732-6e1a-46de-9520-6365c14cc4ca',
			pagesCreated180d: 5287,
			pagesDataAsOf: '2026-04-18',
			pctConnectorsActive28d: 66.7,
			queryDataAsOf: '2026-04-18',
			relDataAsOf: '2026-04-18',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 0,
			teamCount: 76,
			threePConnectorsInstalled: 3,
			totalAppsInstalled: 74,
			totalJiraIssues: 31694,
			totalMinutesRecorded: 3564,
			totalNodes: 8072099,
			totalRelationships: 25013683,
			userCount: 680,
		},
	},
	reddit: {
		key: 'reddit',
		label: messages.datasetRedditLabel,
		description: messages.datasetRedditDescription,
		metrics: {
			activeContributors180d: 40,
			activeNodeTypes: 65,
			activeRelTypes: 215,
			activityDataAsOf: '2026-04-18',
			avgArtifactsPerIssue: 21.2,
			avgDailyJiraCodeLinkChange: 3618,
			avgDailyNodeGrowth: 33617,
			avgDailyRelChange: 169455,
			avgFetchQueriesPerDay: 256704,
			avgFetchQueriesPerSecond: 2.9711,
			avgJiraCodeLinkChangePerSecond: 0.0419,
			avgNodeGrowthPerSecond: 0.3891,
			avgRelChangePerSecond: 1.9613,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 18871,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 14156619,
			connectorObjectsByProvider: [
				{ providerName: 'Zendesk provider', objects30d: 7859462 },
				{ providerName: 'Google Drive provider', objects30d: 4533543 },
				{ providerName: 'Google Calendar', objects30d: 997478 },
				{ providerName: 'GitHub for Jira', objects30d: 545570 },
				{ providerName: 'Slack provider', objects30d: 200175 },
				{ providerName: 'Loom', objects30d: 19429 },
				{ providerName: 'Figma', objects30d: 471 },
				{ providerName: 'Figma for Jira', objects30d: 381 },
				{ providerName: 'pagerduty', objects30d: 74 },
				{ providerName: 'Web Pages Provider', objects30d: 29 },
				{ providerName: 'Salesforce', objects30d: 7 },
			],
			connectorsActive28d: 6,
			connectorsDetail: null,
			connectorsInstalled: 7,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 562,
			issueActivityDataAsOf: '2026-04-18',
			issuesMovedForward180d: 18519,
			jiraCodeLinks: 1553778,
			loomDataAsOf: '2026-04-18T00:34:13.240+00:00',
			loomsRecorded180d: 1941,
			marketplaceAddonsInstalled: 28,
			meetingMinutesSaved: 10366,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 0,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 11,
			organisationId: '9d6f1fce-a96f-4999-9afb-7c1c9b6ee40b',
			pagesCreated180d: 1278,
			pagesDataAsOf: '2026-04-18',
			pctConnectorsActive28d: 85.7,
			queryDataAsOf: '2026-04-18',
			relDataAsOf: '2026-04-18',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 2,
			teamCount: 257,
			threePConnectorsInstalled: 7,
			totalAppsInstalled: 46,
			totalJiraIssues: 96065,
			totalMinutesRecorded: 6911,
			totalNodes: 23338742,
			totalRelationships: 77679572,
			userCount: 447,
		},
	},
	procoreLargest: {
		key: 'procoreLargest',
		label: messages.datasetProcoreLargestLabel,
		description: messages.datasetProcoreLargestDescription,
		metrics: {
			activeContributors180d: 50,
			activeNodeTypes: 77,
			activeRelTypes: 272,
			activityDataAsOf: '2026-04-19',
			avgArtifactsPerIssue: 15.3,
			avgDailyJiraCodeLinkChange: 7253,
			avgDailyNodeGrowth: 36653,
			avgDailyRelChange: 253452,
			avgFetchQueriesPerDay: 397534,
			avgFetchQueriesPerSecond: 4.6011,
			avgJiraCodeLinkChangePerSecond: 0.0839,
			avgNodeGrowthPerSecond: 0.4242,
			avgRelChangePerSecond: 2.9335,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 31176,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 8642100,
			connectorObjectsByProvider: [
				{ providerName: 'Google Drive provider', objects30d: 4509208 },
				{ providerName: 'GitHub for Jira', objects30d: 2735347 },
				{ providerName: 'Workday', objects30d: 1315602 },
				{ providerName: 'Loom', objects30d: 51897 },
				{ providerName: 'Slack provider', objects30d: 11053 },
				{ providerName: 'Figma for Jira', objects30d: 8444 },
				{ providerName: 'Figma', objects30d: 7814 },
				{ providerName: 'LaunchDarkly', objects30d: 2362 },
				{ providerName: 'Jenkins', objects30d: 273 },
				{ providerName: 'Lucid provider', objects30d: 71 },
				{ providerName: 'Power-BI', objects30d: 15 },
				{ providerName: 'Amplitude', objects30d: 6 },
				{ providerName: 'LaunchDarkly for Jira', objects30d: 4 },
				{ providerName: 'Sharepoint provider', objects30d: 3 },
				{ providerName: 'Sentry', objects30d: 1 },
			],
			connectorsActive28d: 2,
			connectorsDetail: null,
			connectorsInstalled: 4,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 2444,
			issueActivityDataAsOf: '2026-04-21',
			issuesMovedForward180d: 42805,
			jiraCodeLinks: 3481503,
			loomDataAsOf: '2026-04-22T23:48:47.695+00:00',
			loomsRecorded180d: 5006,
			marketplaceAddonsInstalled: 46,
			meetingMinutesSaved: 77785,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 31,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 9,
			organisationId: 'd1de2e90-c8b6-4598-9400-85329abae8a8',
			pagesCreated180d: 5299,
			pagesDataAsOf: '2026-04-19',
			pctConnectorsActive28d: 50,
			queryDataAsOf: '2026-04-19',
			relDataAsOf: '2026-04-19',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 2,
			teamCount: 1734,
			threePConnectorsInstalled: 4,
			totalAppsInstalled: 59,
			totalJiraIssues: 287928,
			totalMinutesRecorded: 51856,
			totalNodes: 40400747,
			totalRelationships: 152234806,
			userCount: 771,
		},
	},
	helloFresh: {
		key: 'helloFresh',
		label: messages.datasetHelloFreshLabel,
		description: messages.datasetHelloFreshDescription,
		metrics: {
			activeContributors180d: 5,
			activeNodeTypes: 53,
			activeRelTypes: 182,
			activityDataAsOf: '2026-04-18',
			avgArtifactsPerIssue: 14.9,
			avgDailyJiraCodeLinkChange: 3415,
			avgDailyNodeGrowth: 12374,
			avgDailyRelChange: 63107,
			avgFetchQueriesPerDay: 153295,
			avgFetchQueriesPerSecond: 1.7742,
			avgJiraCodeLinkChangePerSecond: 0.0395,
			avgNodeGrowthPerSecond: 0.1432,
			avgRelChangePerSecond: 0.7304,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 17560,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 2016164,
			connectorObjectsByProvider: [
				{ providerName: 'GitHub for Jira', objects30d: 1862699 },
				{ providerName: 'Google Drive provider', objects30d: 144973 },
				{ providerName: 'Slack provider', objects30d: 4873 },
				{ providerName: 'Figma', objects30d: 2512 },
				{ providerName: 'Miro provider', objects30d: 649 },
				{ providerName: 'Figma for Jira', objects30d: 448 },
				{ providerName: 'Box', objects30d: 8 },
				{ providerName: 'Version Related Links app', objects30d: 2 },
			],
			connectorsActive28d: 0,
			connectorsDetail: null,
			connectorsInstalled: 1,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 2,
			issueActivityDataAsOf: '2026-04-18',
			issuesMovedForward180d: 24590,
			jiraCodeLinks: 2130875,
			loomDataAsOf: null,
			loomsRecorded180d: 0,
			marketplaceAddonsInstalled: 47,
			meetingMinutesSaved: 0,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 1,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 7,
			organisationId: 'bbf8f438-d361-4796-8c68-08df9b370626',
			pagesCreated180d: 1579,
			pagesDataAsOf: '2026-04-18',
			pctConnectorsActive28d: 0,
			queryDataAsOf: '2026-04-18',
			relDataAsOf: '2026-04-18',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 0,
			teamCount: 1427,
			threePConnectorsInstalled: 1,
			totalAppsInstalled: 55,
			totalJiraIssues: 186994,
			totalMinutesRecorded: 0,
			totalNodes: 12155494,
			totalRelationships: 66735838,
			userCount: 634,
		},
	},
	workday: {
		key: 'workday',
		label: messages.datasetWorkdayLabel,
		description: messages.datasetWorkdayDescription,
		metrics: {
			activeContributors180d: 1,
			activeNodeTypes: 48,
			activeRelTypes: 158,
			activityDataAsOf: '2026-04-19',
			avgArtifactsPerIssue: 17.8,
			avgDailyJiraCodeLinkChange: 781,
			avgDailyNodeGrowth: 1942,
			avgDailyRelChange: 14226,
			avgFetchQueriesPerDay: 118663,
			avgFetchQueriesPerSecond: 1.3734,
			avgJiraCodeLinkChangePerSecond: 0.009,
			avgNodeGrowthPerSecond: 0.0225,
			avgRelChangePerSecond: 0.1647,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 8315,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 140593,
			connectorObjectsByProvider: [
				{ providerName: 'GitHub for Jira', objects30d: 81019 },
				{ providerName: 'Google Drive provider', objects30d: 56964 },
				{ providerName: '__unknown__', objects30d: 2196 },
				{ providerName: 'Figma', objects30d: 390 },
				{ providerName: 'Version Related Links app', objects30d: 12 },
				{ providerName: 'Smartsheet provider', objects30d: 12 },
			],
			connectorsActive28d: 0,
			connectorsDetail: null,
			connectorsInstalled: 0,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 0,
			issueActivityDataAsOf: '2026-04-19',
			issuesMovedForward180d: 13336,
			jiraCodeLinks: 400310,
			loomDataAsOf: null,
			loomsRecorded180d: 0,
			marketplaceAddonsInstalled: 23,
			meetingMinutesSaved: 0,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 0,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 7,
			organisationId: 'f98b223b-a1fb-4b98-8b5c-81a6b3360896',
			pagesCreated180d: 174,
			pagesDataAsOf: '2026-04-18',
			pctConnectorsActive28d: 0,
			queryDataAsOf: '2026-04-19',
			relDataAsOf: '2026-04-19',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 0,
			teamCount: 202,
			threePConnectorsInstalled: 0,
			totalAppsInstalled: 30,
			totalJiraIssues: 43146,
			totalMinutesRecorded: 0,
			totalNodes: 1889990,
			totalRelationships: 7445978,
			userCount: 303,
		},
	},
	workdayLargest: {
		key: 'workdayLargest',
		label: messages.datasetWorkdayLargestLabel,
		description: messages.datasetWorkdayLargestDescription,
		metrics: {
			activeContributors180d: 4,
			activeNodeTypes: 52,
			activeRelTypes: 192,
			activityDataAsOf: '2026-04-21',
			avgArtifactsPerIssue: 59.7,
			avgDailyJiraCodeLinkChange: 1457,
			avgDailyNodeGrowth: 4857,
			avgDailyRelChange: 34648,
			avgFetchQueriesPerDay: 75641,
			avgFetchQueriesPerSecond: 0.8755,
			avgJiraCodeLinkChangePerSecond: 0.0169,
			avgNodeGrowthPerSecond: 0.0562,
			avgRelChangePerSecond: 0.401,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 5043,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 610847,
			connectorObjectsByProvider: [
				{ providerName: 'GitHub for Jira', objects30d: 558225 },
				{ providerName: 'Google Drive provider', objects30d: 46406 },
				{ providerName: 'Figma', objects30d: 4864 },
				{ providerName: 'Slack provider', objects30d: 1294 },
				{ providerName: 'Lucid provider', objects30d: 35 },
				{ providerName: 'Salesforce', objects30d: 14 },
				{ providerName: 'Figma for Jira', objects30d: 9 },
			],
			connectorsActive28d: 0,
			connectorsDetail: null,
			connectorsInstalled: 1,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 422,
			issueActivityDataAsOf: '2026-04-21',
			issuesMovedForward180d: 4785,
			jiraCodeLinks: 1128522,
			loomDataAsOf: null,
			loomsRecorded180d: 0,
			marketplaceAddonsInstalled: 31,
			meetingMinutesSaved: 0,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 0,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 9,
			organisationId: 'c175835a-1029-4b03-bda0-601b1a101710',
			pagesCreated180d: 449,
			pagesDataAsOf: '2026-04-21',
			pctConnectorsActive28d: 0,
			queryDataAsOf: '2026-04-21',
			relDataAsOf: '2026-04-19',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 0,
			teamCount: 101,
			threePConnectorsInstalled: 1,
			totalAppsInstalled: 41,
			totalJiraIssues: 21202,
			totalMinutesRecorded: 0,
			totalNodes: 3185817,
			totalRelationships: 20547334,
			userCount: 67,
		},
	},
};

/**
 * Ordered list of static datasets, used by the debug panel UI to render the
 * selectable list in the order Atlassian designers laid them out on the
 * source Confluence page.
 */
export const STATIC_TIER2_DATASET_LIST: ReadonlyArray<StaticTier2Dataset> = [
	STATIC_TIER2_DATASETS.fastMed,
	STATIC_TIER2_DATASETS.accenture,
	STATIC_TIER2_DATASETS.allegion,
	STATIC_TIER2_DATASETS.atlassian,
	STATIC_TIER2_DATASETS.communardo,
	STATIC_TIER2_DATASETS.reddit,
	STATIC_TIER2_DATASETS.procoreLargest,
	STATIC_TIER2_DATASETS.helloFresh,
	STATIC_TIER2_DATASETS.workday,
	STATIC_TIER2_DATASETS.workdayLargest,
];

///////////////////////////////////////
/////////      TIER 3      ////////////
///////////////////////////////////////
/**
 * Identifier for a static Tier 3 (getting-started) dataset. Selecting one
 * of these routes the UI to `GettingStartedView`. The view itself is a
 * pure capability showcase and does not read numeric data from the
 * selected dataset — the datasets exist only to let debug-panel users
 * preview a few recognisable "early-days tenant" scenarios by name.
 */
export type StaticTier3DatasetKey = 'brandNewTenant' | 'trialTenant' | 'soloTenant';

/**
 * UI-facing description of a single static Tier 3 dataset.
 */
export type StaticTier3Dataset = {
	key: StaticTier3DatasetKey;
	label: MessageDescriptor;
	description: MessageDescriptor;
	metrics: OrgMetricsResponse;
};

/**
 * Static Tier 3 datasets. `GettingStartedView` renders a capability
 * showcase that intentionally ignores the numeric payload (it always
 * passes `null` into `selectMetrics`), so these datasets carry no
 * `metrics` payload — they are label/description pairs used purely to
 * give debug-panel users a few named "early-days tenant" scenarios to
 * choose between when previewing the Tier 3 view.
 */
export const STATIC_TIER3_DATASETS: Record<StaticTier3DatasetKey, StaticTier3Dataset> = {
	brandNewTenant: {
		key: 'brandNewTenant',
		label: messages.datasetTier3BrandNewLabel,
		description: messages.datasetTier3BrandNewDescription,
		metrics: {
			activeContributors180d: -1,
			activeNodeTypes: 71,
			activeRelTypes: 235,
			activityDataAsOf: '2026-04-17',
			avgArtifactsPerIssue: 0.7,
			avgDailyJiraCodeLinkChange: 1107,
			avgDailyNodeGrowth: 70638,
			avgDailyRelChange: 643225,
			avgFetchQueriesPerDay: 389,
			avgFetchQueriesPerSecond: 0.0164,
			avgJiraCodeLinkChangePerSecond: 0.0128,
			avgNodeGrowthPerSecond: 0.8176,
			avgRelChangePerSecond: 7.4447,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 99,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 790,
			connectorObjectsByProvider: [
				{ providerName: 'Slack provider', objects30d: 719 },
				{ providerName: 'Figma', objects30d: 71 },
			],
			connectorsActive28d: 0,
			connectorsDetail: null,
			connectorsInstalled: 0,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 4919,
			issueActivityDataAsOf: '2026-04-21',
			issuesMovedForward180d: 49,
			jiraCodeLinks: 4,
			loomDataAsOf: '2026-04-21T12:14:21.319+00:00',
			loomsRecorded180d: 19,
			marketplaceAddonsInstalled: 292,
			meetingMinutesSaved: 48,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 0,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 13,
			organisationId: 'a53d6b63-3k57-1408-j79d-0b60b08kb685',
			pagesCreated180d: 34,
			pagesDataAsOf: '2026-04-18',
			pctConnectorsActive28d: 0,
			queryDataAsOf: null,
			relDataAsOf: '2026-04-18',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 0,
			teamCount: 4,
			threePConnectorsInstalled: 0,
			totalAppsInstalled: 305,
			totalJiraIssues: 4844413,
			totalMinutesRecorded: 32,
			totalNodes: 24456310,
			totalRelationships: 107366741,
			userCount: 12358,
		},
	},
	trialTenant: {
		key: 'trialTenant',
		label: messages.datasetTier3TrialLabel,
		description: messages.datasetTier3TrialDescription,
		metrics: {
			activeContributors180d: -1,
			activeNodeTypes: 71,
			activeRelTypes: 235,
			activityDataAsOf: '2026-04-17',
			avgArtifactsPerIssue: 0.7,
			avgDailyJiraCodeLinkChange: 1107,
			avgDailyNodeGrowth: 70638,
			avgDailyRelChange: 643225,
			avgFetchQueriesPerDay: 999,
			avgFetchQueriesPerSecond: 0.0164,
			avgJiraCodeLinkChangePerSecond: 0.0128,
			avgNodeGrowthPerSecond: 0.8176,
			avgRelChangePerSecond: 7.4447,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 99,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 790,
			connectorObjectsByProvider: [
				{ providerName: 'Slack provider', objects30d: 719 },
				{ providerName: 'Figma', objects30d: 71 },
			],
			connectorsActive28d: 0,
			connectorsDetail: null,
			connectorsInstalled: 0,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 4919,
			issueActivityDataAsOf: '2026-04-21',
			issuesMovedForward180d: 36,
			jiraCodeLinks: 4,
			loomDataAsOf: '2026-04-21T12:14:21.319+00:00',
			loomsRecorded180d: 19,
			marketplaceAddonsInstalled: 292,
			meetingMinutesSaved: 48,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 0,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 13,
			organisationId: 'a53d6b63-3k57-1408-j79d-0b60b08kb685',
			pagesCreated180d: 49,
			pagesDataAsOf: '2026-04-18',
			pctConnectorsActive28d: 0,
			queryDataAsOf: null,
			relDataAsOf: '2026-04-18',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 0,
			teamCount: 4,
			threePConnectorsInstalled: 0,
			totalAppsInstalled: 305,
			totalJiraIssues: 4844413,
			totalMinutesRecorded: 32,
			totalNodes: 24456310,
			totalRelationships: 107366741,
			userCount: 12358,
		},
	},
	soloTenant: {
		key: 'soloTenant',
		label: messages.datasetTier3SoloLabel,
		description: messages.datasetTier3SoloDescription,
		metrics: {
			activeContributors180d: -1,
			activeNodeTypes: 71,
			activeRelTypes: 235,
			activityDataAsOf: '2026-04-17',
			avgArtifactsPerIssue: 0.7,
			avgDailyJiraCodeLinkChange: 1107,
			avgDailyNodeGrowth: 70638,
			avgDailyRelChange: 643225,
			avgFetchQueriesPerDay: 714,
			avgFetchQueriesPerSecond: 0.0164,
			avgJiraCodeLinkChangePerSecond: 0.0128,
			avgNodeGrowthPerSecond: 0.8176,
			avgRelChangePerSecond: 7.4447,
			cacheStatus: null,
			collaboratorTeams: null,
			collaboratorTeamsTruncated: null,
			collaborators: null,
			commentsCreated180d: 99,
			commentsDataAsOf: null,
			computedAt: COMPUTED_AT,
			connectorDataAsOf: null,
			connectorObjects180d: 790,
			connectorObjectsByProvider: [
				{ providerName: 'Slack provider', objects30d: 719 },
				{ providerName: 'Figma', objects30d: 71 },
			],
			connectorsActive28d: 0,
			connectorsDetail: null,
			connectorsInstalled: 0,
			daysSinceSnapshot: null,
			entitiesTouched: null,
			estimatedNodesNow: null,
			forgeAppsInstalled: null,
			goalWorkItemLinks: 4919,
			issueActivityDataAsOf: '2026-04-21',
			issuesMovedForward180d: 21,
			jiraCodeLinks: 4,
			loomDataAsOf: '2026-04-21T12:14:21.319+00:00',
			loomsRecorded180d: 19,
			marketplaceAddonsInstalled: 292,
			meetingMinutesSaved: 48,
			meetingNotesDataAsOf: null,
			meetingNotesWithRecording180d: 0,
			nodeDataAsOf: '2026-04-18',
			onePAppsInstalled: 13,
			organisationId: 'a53d6b63-3k57-1408-j79d-0b60b08kb685',
			pagesCreated180d: 52,
			pagesDataAsOf: '2026-04-18',
			pctConnectorsActive28d: 0,
			queryDataAsOf: null,
			relDataAsOf: '2026-04-18',
			smartlinkDataAsOf: null,
			smartlinkPastes180d: 0,
			teamCount: 4,
			threePConnectorsInstalled: 0,
			totalAppsInstalled: 305,
			totalJiraIssues: 4844413,
			totalMinutesRecorded: 32,
			totalNodes: 24456310,
			totalRelationships: 107366741,
			userCount: 12358,
		},
	},
};

/**
 * Ordered list of Tier 3 datasets for the debug panel UI.
 */
export const STATIC_TIER3_DATASET_LIST: ReadonlyArray<StaticTier3Dataset> = [
	STATIC_TIER3_DATASETS.brandNewTenant,
	STATIC_TIER3_DATASETS.trialTenant,
	STATIC_TIER3_DATASETS.soloTenant,
];

///////////////////////////////////////
/////////      USERS      ////////////
///////////////////////////////////////
/**
 * Static collaboration-graph response served by `CollaborationUsersAtom`
 * whenever the debug panel is in "static dataset" mode. The same five
 * recommended users are returned for every static dataset — the metrics
 * payload swaps per organisation, but the collaborators stay constant so
 * that demo flows always show the same recognisable Teamwork Graph team.
 */
export const STATIC_COLLABORATION_USERS: ReadonlyArray<CollabGraphRecommendedEntity> = [
	{
		score: 0.5758,
		id: 'ari:cloud:identity::user/712020:fe8c7aeb-92b9-4075-bfed-e4d859bb587f',
		expand: {
			ari: 'ari:cloud:identity::user/712020:fe8c7aeb-92b9-4075-bfed-e4d859bb587f',
			account_id: '712020:fe8c7aeb-92b9-4075-bfed-e4d859bb587f',
			email: 'acherk@atlassian.com',
			name: 'Alvin Cherk',
			picture:
				'https://secure.gravatar.com/avatar/527c98c2e4b0cf1c9a2bc979886644a4?d=https%3A%2F%2Favatar-management--avatars.us-west-2.prod.public.atl-paas.net%2Finitials%2FAC-6.png',
		},
	},
	{
		score: 0.3358,
		id: 'ari:cloud:identity::user/70121:f604320b-d57f-4347-9af4-6134863803c2',
		expand: {
			ari: 'ari:cloud:identity::user/70121:f604320b-d57f-4347-9af4-6134863803c2',
			account_id: '70121:f604320b-d57f-4347-9af4-6134863803c2',
			email: 'hshahid@atlassian.com',
			name: 'Hamza Shahid',
			picture:
				'https://avatar-management--avatars.us-west-2.prod.public.atl-paas.net/70121:f604320b-d57f-4347-9af4-6134863803c2/371838a1-9613-4320-8e6e-b0542872590a/128',
		},
	},
	{
		score: 0.3259,
		id: 'ari:cloud:identity::user/63749bac9960988ef6bc6096',
		expand: {
			ari: 'ari:cloud:identity::user/63749bac9960988ef6bc6096',
			account_id: '63749bac9960988ef6bc6096',
			email: 'efernando@atlassian.com',
			name: 'Eve Fernando',
			picture:
				'https://avatar-management--avatars.us-west-2.prod.public.atl-paas.net/63749bac9960988ef6bc6096/835e5c9d-bb4d-4976-8408-03419a49b5c1/128',
		},
	},
	{
		score: 0.3024,
		id: 'ari:cloud:identity::user/712020:538b5dc0-5d53-4703-95be-0803cdd14327',
		expand: {
			ari: 'ari:cloud:identity::user/712020:538b5dc0-5d53-4703-95be-0803cdd14327',
			account_id: '712020:538b5dc0-5d53-4703-95be-0803cdd14327',
			email: 'shaq@atlassian.com',
			name: 'Shan Haq',
			picture:
				'https://avatar-management--avatars.us-west-2.prod.public.atl-paas.net/712020:538b5dc0-5d53-4703-95be-0803cdd14327/47074604-003e-4efd-a650-4b14283b5a5f/128',
		},
	},
	{
		score: 0.2296,
		id: 'ari:cloud:identity::user/63749bf65fce844d606712e6',
		expand: {
			ari: 'ari:cloud:identity::user/63749bf65fce844d606712e6',
			account_id: '63749bf65fce844d606712e6',
			email: 'ndutta2@atlassian.com',
			name: 'Neel Dutta',
			picture:
				'https://avatar-management--avatars.us-west-2.prod.public.atl-paas.net/63749bf65fce844d606712e6/c8f3eb84-f065-4e52-92c5-baac27ed7e6f/128',
		},
	},
];
